<template><div><code-well-header>
  <dt-loader></dt-loader>
</code-well-header>
<h2 id="loader-vs-skeleton" tabindex="-1" class="d-docsite--header-2">Loader vs Skeleton <a class="header-anchor d-link d-docsite--link d-link" href="#loader-vs-skeleton" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph"><strong>Use Loader when:</strong></p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">You need a simple, generic loading indicator</li>
<li class="d-docsite--list-element">Loading time is short or indeterminate</li>
<li class="d-docsite--list-element">You want to indicate that a process is running without showing layout structure</li>
<li class="d-docsite--list-element">You need a compact loading indicator for buttons, small components, or overlays</li>
</ul>
<p class="d-docsite--paragraph"><strong>Use <a href="/components/skeleton.html" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">Skeleton</a> when:</strong></p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Content is expected to take more than a few hundred milliseconds to load on average</li>
<li class="d-docsite--list-element">You want to show the approximate layout and structure of content being loaded</li>
<li class="d-docsite--list-element">Loading complex lists, cards, or detailed content where users benefit from seeing the expected layout</li>
<li class="d-docsite--list-element">You need to maintain visual hierarchy during loading states</li>
</ul>
<h2 id="variants" tabindex="-1" class="d-docsite--header-2">Variants <a class="header-anchor d-link d-docsite--link d-link" href="#variants" aria-hidden="true">#</a></h2>
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The base loader should be the go-to loader for most of your needs. When in doubt, use this style.</p>
<code-well-header>
  <dt-loader></dt-loader>
</code-well-header>
<code-example-tabs
htmlCode='
<div aria-label="loading" class="d-loader" size="500">
  <svg class="d-icon d-loader__icon d-icon--size-500">...</svg>
</div>
'
vueCode='
<dt-loader />
'
showHtmlWarning />
<h2 id="sizes" tabindex="-1" class="d-docsite--header-2">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The base loader size is 24px and should be used in most cases.</p>
<code-well-header>
  <dt-stack
    gap="400"
    :direction="{ 'default': 'column', 'md': 'row' }"
  >
    <dt-loader size="100"></dt-loader>
    <dt-loader size="200"></dt-loader>
    <dt-loader size="300"></dt-loader>
    <dt-loader size="400"></dt-loader>
    <dt-loader size="500"></dt-loader>
    <dt-loader size="600"></dt-loader>
    <dt-loader size="700"></dt-loader>
    <dt-loader size="800"></dt-loader>
  </dt-stack>
</code-well-header>
<code-example-tabs
htmlCode='
<div aria-label="loading" class="d-loader" size="100">
  <svg class="d-icon d-loader__icon d-icon--size-100">...</svg>
</div>
<div aria-label="loading" class="d-loader" size="200">
  <svg class="d-icon d-loader__icon d-icon--size-200">...</svg>
</div>
<div aria-label="loading" class="d-loader" size="300">
  <svg class="d-icon d-loader__icon d-icon--size-300">...</svg>
</div>
<div aria-label="loading" class="d-loader" size="400">
  <svg class="d-icon d-loader__icon d-icon--size-400">...</svg>
</div>
<div aria-label="loading" class="d-loader" size="500">
  <svg class="d-icon d-loader__icon d-icon--size-500">...</svg>
</div>
<div aria-label="loading" class="d-loader" size="600">
  <svg class="d-icon d-loader__icon d-icon--size-600">...</svg>
</div>
<div aria-label="loading" class="d-loader" size="700">
  <svg class="d-icon d-loader__icon d-icon--size-700">...</svg>
</div>
<div aria-label="loading" class="d-loader" size="800">
  <svg class="d-icon d-loader__icon d-icon--size-800">...</svg>
</div>
'
vueCode='
<dt-loader size="100"></dt-loader>
<dt-loader size="200"></dt-loader>
<dt-loader size="300"></dt-loader>
<dt-loader size="400"></dt-loader>
<dt-loader></dt-loader>
<dt-loader size="600"></dt-loader>
<dt-loader size="700"></dt-loader>
<dt-loader size="800"></dt-loader>
'
showHtmlWarning />
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="loader" /><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">You can add an <code v-pre>aria-label</code> attribute to the loader to indicate a custom label.</li>
<li class="d-docsite--list-element">If no <code v-pre>aria-label</code> is provided, the default value is &quot;loading&quot;.</li>
</ul>
</div></template>


